import React from 'react';
import {
  HiPhoto,
  HiDocumentText,
  HiTableCells,
  HiPresentationChartBar,
  HiFilm,
  HiMusicalNote,
  HiArchiveBox,
  HiCodeBracket,
  HiFolder,
  HiDocument,
} from 'react-icons/hi2';
import { StorageDirectory, StorageItem, SortBy, SortOrder } from '../types/storage';

/**
 * Format bytes to human-readable file size.
 * @param bytes - Size in bytes
 * @param decimals - Number of decimal places (default: 2)
 * @returns Formatted string (e.g., "1.5 MB", "350 KB")
 */
export function formatFileSize(bytes: number, decimals: number = 2): string {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / k ** i).toFixed(dm))} ${sizes[i]}`;
}

/**
 * Get file extension from filename.
 */
export function getFileExtension(fileName: string): string {
  const lastDot = fileName.lastIndexOf('.');
  if (lastDot === -1) return '';
  return fileName.substring(lastDot + 1).toLowerCase();
}

/**
 * Get file type category from extension.
 */
export function getFileTypeCategory(fileName: string): string {
  const extension = getFileExtension(fileName);

  const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp', 'ico'];
  const documentExtensions = ['pdf', 'doc', 'docx', 'txt', 'rtf', 'odt'];
  const spreadsheetExtensions = ['xls', 'xlsx', 'csv', 'ods'];
  const presentationExtensions = ['ppt', 'pptx', 'odp'];
  const videoExtensions = ['mp4', 'avi', 'mov', 'wmv', 'flv', 'mkv', 'webm'];
  const audioExtensions = ['mp3', 'wav', 'ogg', 'flac', 'aac', 'm4a'];
  const archiveExtensions = ['zip', 'rar', '7z', 'tar', 'gz', 'bz2'];
  const codeExtensions = ['js', 'ts', 'tsx', 'jsx', 'py', 'java', 'cpp', 'c', 'cs', 'php', 'rb', 'go', 'rs', 'swift'];

  if (imageExtensions.includes(extension)) return 'image';
  if (documentExtensions.includes(extension)) return 'document';
  if (spreadsheetExtensions.includes(extension)) return 'spreadsheet';
  if (presentationExtensions.includes(extension)) return 'presentation';
  if (videoExtensions.includes(extension)) return 'video';
  if (audioExtensions.includes(extension)) return 'audio';
  if (archiveExtensions.includes(extension)) return 'archive';
  if (codeExtensions.includes(extension)) return 'code';

  return 'file';
}

/**
 * Get icon component for file type.
 */
export function getFileIcon(fileName: string): JSX.Element {
  const category = getFileTypeCategory(fileName);

  const iconMap: Record<string, JSX.Element> = {
    image: <HiPhoto className="w-6 h-6 text-purple-500" />,
    document: <HiDocumentText className="w-6 h-6 text-blue-500" />,
    spreadsheet: <HiTableCells className="w-6 h-6 text-green-500" />,
    presentation: <HiPresentationChartBar className="w-6 h-6 text-orange-500" />,
    video: <HiFilm className="w-6 h-6 text-red-500" />,
    audio: <HiMusicalNote className="w-6 h-6 text-pink-500" />,
    archive: <HiArchiveBox className="w-6 h-6 text-yellow-600" />,
    code: <HiCodeBracket className="w-6 h-6 text-indigo-500" />,
    folder: <HiFolder className="w-6 h-6 text-secondary" />,
    file: <HiDocument className="w-6 h-6 text-gray-500" />,
  };

  return iconMap[category] || iconMap.file;
}

/**
 * Get folder icon component.
 */
export function getFolderIcon(): JSX.Element {
  return <HiFolder className="w-6 h-6 text-secondary" />;
}

/**
 * Check if filename is valid (no special characters).
 */
export function isValidFileName(name: string): boolean {
  if (!name || name.trim().length === 0) return false;
  // Disallow: / \ : * ? " < > |
  const invalidChars = /[/\\:*?"<>|]/;
  return !invalidChars.test(name);
}

/**
 * Check if item name already exists in directory.
 */
export function isDuplicateName(
  name: string,
  items: StorageItem[],
  directories: StorageDirectory[],
  excludeId?: number,
  excludeType?: 'item' | 'directory'
): boolean {
  const normalizedName = name.toLowerCase().trim();

  const hasItemDuplicate = items.some(
    (item) =>
      item.fileName.toLowerCase() === normalizedName &&
      !(excludeType === 'item' && item.id === excludeId)
  );

  const hasDirDuplicate = directories.some(
    (dir) =>
      dir.name.toLowerCase() === normalizedName &&
      !(excludeType === 'directory' && dir.id === excludeId)
  );

  return hasItemDuplicate || hasDirDuplicate;
}

/**
 * Sort items by criteria.
 */
export function sortItems(items: StorageItem[], sortBy: SortBy, sortOrder: SortOrder): StorageItem[] {
  const sorted = [...items].sort((a, b) => {
    let comparison = 0;

    switch (sortBy) {
      case 'name':
        comparison = a.fileName.localeCompare(b.fileName);
        break;
      case 'date':
        comparison = new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime();
        break;
      case 'size':
        comparison = a.fileSizeInBytes - b.fileSizeInBytes;
        break;
      default:
        comparison = 0;
    }

    return sortOrder === 'asc' ? comparison : -comparison;
  });

  return sorted;
}

/**
 * Sort directories by name.
 */
export function sortDirectories(
  directories: StorageDirectory[],
  sortBy: SortBy,
  sortOrder: SortOrder
): StorageDirectory[] {
  const sorted = [...directories].sort((a, b) => {
    let comparison = 0;

    switch (sortBy) {
      case 'name':
        comparison = a.name.localeCompare(b.name);
        break;
      case 'date':
        comparison = new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime();
        break;
      case 'size':
      default:
        comparison = 0;
        break;
    }

    return sortOrder === 'asc' ? comparison : -comparison;
  });

  return sorted;
}

/**
 * Build breadcrumb path from directory tree.
 */
export function buildBreadcrumbs(
  currentDirectoryId: number | null,
  allDirectories: StorageDirectory[]
): Array<{ id: number | null; name: string }> {
  const breadcrumbs: Array<{ id: number | null; name: string }> = [
    { id: null, name: 'My Files' },
  ];

  if (currentDirectoryId === null) return breadcrumbs;

  const dirMap = new Map(allDirectories.map((d) => [d.id, d]));
  
  const pathIds: number[] = [];
  let currentId: number | null = currentDirectoryId;

  while (currentId !== null) {
    pathIds.unshift(currentId);
    const currentDir = dirMap.get(currentId);
    if (!currentDir) break;
    currentId = currentDir.parentId;
  }

  pathIds.forEach((id) => {
    const dir = dirMap.get(id);
    if (dir) {
      breadcrumbs.push({ id: dir.id, name: dir.name });
    }
  });

  return breadcrumbs;
}

/**
 * Get all children directory IDs recursively.
 */
export function getAllChildDirectoryIds(
  directoryId: number,
  allDirectories: StorageDirectory[]
): number[] {
  const childIds: number[] = [directoryId];

  const findChildren = (parentId: number) => {
    const children = allDirectories.filter((d) => d.parentId === parentId);
    children.forEach((child) => {
      childIds.push(child.id);
      findChildren(child.id);
    });
  };

  findChildren(directoryId);

  return childIds;
}

/**
 * Check if storage limit would be exceeded by upload.
 */
export function wouldExceedStorageLimit(
  currentUsed: number,
  maxStorage: number,
  uploadSize: number
): boolean {
  return currentUsed + uploadSize > maxStorage;
}

/**
 * Calculate storage percentage used.
 */
export function calculateStoragePercentage(used: number, max: number): number {
  if (max === 0) return 0;
  return Math.min(Math.round((used / max) * 100), 100);
}

/**
 * Get storage warning level.
 * Returns 'normal', 'warning', or 'critical'
 */
export function getStorageWarningLevel(percentage: number): 'normal' | 'warning' | 'critical' {
  if (percentage >= 100) return 'critical';
  if (percentage >= 90) return 'warning';
  return 'normal';
}

/**
 * Filter items by search query.
 */
export function filterItemsBySearch(items: StorageItem[], query: string): StorageItem[] {
  if (!query.trim()) return items;
  const lowerQuery = query.toLowerCase();
  return items.filter((item) => item.fileName.toLowerCase().includes(lowerQuery));
}

/**
 * Filter directories by search query.
 */
export function filterDirectoriesBySearch(
  directories: StorageDirectory[],
  query: string
): StorageDirectory[] {
  if (!query.trim()) return directories;
  const lowerQuery = query.toLowerCase();
  return directories.filter((dir) => dir.name.toLowerCase().includes(lowerQuery));
}

/**
 * Check if directory can be moved to target (prevent circular references).
 */
export function canMoveDirectory(
  directoryId: number,
  targetParentId: number | null,
  allDirectories: StorageDirectory[]
): boolean {
  // Can't move to itself
  if (directoryId === targetParentId) return false;

  // Can move to root
  if (targetParentId === null) return true;

  // Check if target is a descendant of the directory being moved
  const childIds = getAllChildDirectoryIds(directoryId, allDirectories);
  return !childIds.includes(targetParentId);
}

/**
 * Get total size of files in a directory (non-recursive).
 */
export function getDirectorySize(directoryId: number | null, items: StorageItem[]): number {
  return items
    .filter((item) => item.directoryId === directoryId)
    .reduce((sum, item) => sum + item.fileSizeInBytes, 0);
}

/**
 * Format date to relative or absolute format.
 */
export function formatDate(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;

  return date.toLocaleDateString();
}

/**
 * Get file name without extension.
 */
export function getFileNameWithoutExtension(fileName: string): string {
  const lastDot = fileName.lastIndexOf('.');
  if (lastDot === -1) return fileName;
  return fileName.substring(0, lastDot);
}

/**
 * Build file name with new name but keep extension.
 */
export function buildFileNameWithExtension(newName: string, originalFileName: string): string {
  const extension = getFileExtension(originalFileName);
  if (!extension) return newName;
  return `${newName}.${extension}`;
}